import { AbstractControl, ValidationErrors } from '@angular/forms';
import moment from 'moment';
import { TimeRangeType } from 'src/app/dashboards/enumerations/time-range-type';
import { TelemetryWidgetTimeRange } from 'src/app/dashboards/widgets-library/telemetry/telemetry-widget-time-range';

import { map, Observable } from 'rxjs';
import { FormGroupForWarnings } from '../classes/form-group-for-warnings';
import { last1Year, last1YearInDays, last2Years, last2YearsInDays } from '../constants/telemetry.constants';
import { TranslationService } from '../transloco/services/translation.service';

export const getTimeDiffInSeconds = (upperBound: Date, lowerBound: Date) => {
    return moment(upperBound).diff(moment(lowerBound), 'seconds');
};

export const timeSpanValidator = (translationService: TranslationService) => {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
        const formGroup = control as FormGroupForWarnings;
        const errors = {} as ValidationErrors;
        formGroup.resetWarnings?.();

        const timeRangeType = formGroup.controls['timeRangeType']?.value;
        const timeRange = formGroup.controls['timeRange']?.value;
        const startTimeControl = formGroup.controls['startTime'];
        const endTimeControl = formGroup.controls['endTime'];

        let { startTime, endTime } = getTimeSpan(timeRange, timeRangeType, startTimeControl, endTimeControl);

        return translationService.afterLanguageChanged$.pipe(
            map(() => {
                validateTimeRangeSettings(translationService, errors, timeRangeType, timeRange);

                validateStartAndEndDate(translationService, errors, startTime, endTime);

                validateCrossDay(translationService, endTime, startTime, timeRangeType, timeRange, formGroup);

                validateSameOrBefore(translationService, endTime, startTime, timeRangeType, errors);

                validateMaxAllowedTimeSpan(translationService, endTime, startTime, errors);

                validateEarliestStartDate(translationService, startTime, errors);

                const errors_ = Object.keys(errors).length > 0 ? errors : null;

                formGroup.setErrors(errors_);
                return errors_;
            })
        );
    };
};

const getTimeSpan = (timeRange: TelemetryWidgetTimeRange, timeRangeType: unknown, startTimeControl: AbstractControl, endTimeControl: AbstractControl) => {
    let startTime, endTime;

    if (timeRange >= TelemetryWidgetTimeRange.Today && timeRange <= TelemetryWidgetTimeRange.Sunday && timeRangeType === TimeRangeType.Predefined) {
        const today = moment().utc();
        startTime = moment(startTimeControl.value).set({ year: today.year(), month: today.month(), days: today.days() }).utc();
        endTime = moment(endTimeControl.value).set({ year: today.year(), month: today.month(), days: today.days() }).utc();
    } else {
        startTime = moment(startTimeControl.value).utc();
        endTime = moment(endTimeControl.value).utc();
    }

    return { startTime, endTime };
};

const validateTimeRangeSettings = (translationService: TranslationService, errors: ValidationErrors, timeRangeType: TimeRangeType, timeRange: TelemetryWidgetTimeRange): void => {
    if (timeRangeType === TimeRangeType.Predefined && timeRange == null) {
        errors['timeRangeRequired'] = translationService.translate('defaults.validation.timeRangeRequired');
    }
};

const validateStartAndEndDate = (translationService: TranslationService, errors: ValidationErrors, startTime: moment.Moment, endTime: moment.Moment) => {
    if (!startTime.isValid()) {
        errors['invalidStartTime'] = translationService.translate('defaults.validation.invalidStartTime');
    }

    if (!endTime.isValid()) {
        errors['invalidEndTime'] = translationService.translate('defaults.validation.invalidEndTime');
    }
};

export const validateCrossDay = (translationService: TranslationService, endTime: moment.Moment, startTime: moment.Moment, timeRangeType: TimeRangeType, timeRange: TelemetryWidgetTimeRange, formGroup: FormGroupForWarnings) => {
    if (endTime.isSameOrBefore(startTime) && timeRangeType === TimeRangeType.Predefined) {
        const weekDays: Map<TelemetryWidgetTimeRange, string> = new Map([
            [TelemetryWidgetTimeRange.Today, translationService.translate('time.today')],
            [TelemetryWidgetTimeRange.Yesterday, translationService.translate('time.yesterday')],
            [TelemetryWidgetTimeRange.Monday, translationService.translate('time.monday')],
            [TelemetryWidgetTimeRange.Tuesday, translationService.translate('time.tuesday')],
            [TelemetryWidgetTimeRange.Wednesday, translationService.translate('time.wednesday')],
            [TelemetryWidgetTimeRange.Thursday, translationService.translate('time.thursday')],
            [TelemetryWidgetTimeRange.Friday, translationService.translate('time.friday')],
            [TelemetryWidgetTimeRange.Saturday, translationService.translate('time.saturday')],
            [TelemetryWidgetTimeRange.Sunday, translationService.translate('time.sunday')],
        ]);

        const followingDays: Map<TelemetryWidgetTimeRange, string> = new Map([
            [TelemetryWidgetTimeRange.Today, translationService.translate('time.tomorrow')],
            [TelemetryWidgetTimeRange.Yesterday, translationService.translate('time.today')],
            [TelemetryWidgetTimeRange.Monday, translationService.translate('time.tuesday')],
            [TelemetryWidgetTimeRange.Tuesday, translationService.translate('time.wednesday')],
            [TelemetryWidgetTimeRange.Wednesday, translationService.translate('time.thursday')],
            [TelemetryWidgetTimeRange.Thursday, translationService.translate('time.friday')],
            [TelemetryWidgetTimeRange.Friday, translationService.translate('time.saturday')],
            [TelemetryWidgetTimeRange.Saturday, translationService.translate('time.sunday')],
            [TelemetryWidgetTimeRange.Sunday, translationService.translate('time.monday')],
        ]);

        const startDayName = weekDays.get(timeRange);
        const endDayName = followingDays.get(timeRange);

        formGroup.warnings['isCrossDay'] = translationService.translate('hints.isCrossDay', { startDayName, startTime: startTime.local().format('HH:mm'), endDayName, endTime: endTime.local().format('HH:mm') });
    }
};

const validateSameOrBefore = (translationService: TranslationService, endTime: moment.Moment, startTime: moment.Moment, timeRangeType: unknown, errors: ValidationErrors) => {
    if (endTime.isSameOrBefore(startTime) && timeRangeType !== TimeRangeType.Predefined) {
        errors['isSameOrBefore'] = translationService.translate('defaults.validation.isTimeSameOrBefore');
    }
};

const validateMaxAllowedTimeSpan = (translationService: TranslationService, endTime: moment.Moment, startTime: moment.Moment, errors: ValidationErrors) => {
    const timeSpanInDays = endTime.diff(startTime, 'days');

    if (timeSpanInDays > last1YearInDays.duration) {
        errors['timeSpanExceeded'] = translationService.translate('defaults.validation.timeSpanExceeded', { duration: last1Year.duration });
    }
};

const validateEarliestStartDate = (translationService: TranslationService, startTime: moment.Moment, errors: ValidationErrors) => {
    const earliestAllowedStartDate = moment().subtract(last2YearsInDays.duration, last2YearsInDays.unit);

    if (startTime.isBefore(earliestAllowedStartDate, 'minute')) {
        errors['startTimeTooEarly'] = translationService.translate('defaults.validation.startTimeTooEarly', { duration: last2Years.duration, unit: last2Years.unit });
    }
};
